<template>
  <div ref="wrapperRef" :style="style" :class="containerKls">
    <!-- icon & line -->
    <div :class="[ns.e('head'), ns.is(currentStatus)]">
      <div v-if="!isSimple" :class="ns.e('line')" :style="lineParentStyle">
        <i :class="ns.e('line-inner')" :style="lineStyle" />
      </div>
      <div style="display: flex; align-items: center; flex-direction: row">
        <div
          ref="iconRef"
          :class="[ns.e('icon'), ns.is(icon || $slots.icon ? 'icon' : 'text')]"
        >
          <slot name="icon">
            <lt-icon v-if="icon" :class="ns.e('icon-inner')">
              <component :is="icon" />
            </lt-icon>
            <lt-icon
              v-else-if="currentStatus === 'success'"
              :class="[ns.e('icon-inner'), ns.is('status')]"
            >
              <Check />
            </lt-icon>
            <lt-icon
              v-else-if="currentStatus === 'error'"
              :class="[ns.e('icon-inner'), ns.is('status')]"
            >
              <Close />
            </lt-icon>
            <div v-else-if="!isSimple" :class="ns.e('icon-inner')">
              {{ index + 1 }}
            </div>
          </slot>
        </div>
        <div
          ref="contentRef"
          :class="[ns.e('title'), ns.is(currentStatus)]"
          :style="{
            width: parent.props.direction === 'vertical' ? '50px' : 'auto',
            marginLeft: '5px',
          }"
        >
          <slot name="title">{{ title }}</slot>
        </div>
      </div>
    </div>
    <!-- title & description -->
    <div :class="ns.e('main')">
      <!-- <div :class="[ns.e('title'), ns.is(currentStatus)]">
        <slot name="title">{{ title }}</slot>
      </div> -->
      <div v-if="isSimple" :class="ns.e('arrow')" />
      <div v-else :class="[ns.e('description'), ns.is(currentStatus)]">
        <slot name="description">{{ description }}</slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  computed,
  getCurrentInstance,
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue'
import { LtIcon } from '@lit-element/components/icon'
import { useNamespace } from '@lit-element/hooks'
import { isNumber } from '@lit-element/utils'
import { Check, Close } from '@inte-cloud/icons-vue'
import { stepProps } from './item'

import type { CSSProperties, Ref } from 'vue'
export interface IStepsProps {
  space: number | string
  active: number
  direction: string
  alignCenter: boolean
  simple: boolean
  finishStatus: string
  processStatus: string
}

export interface StepItemState {
  uid: number
  currentStatus: string
  setIndex: (val: number) => void
  calcProgress: (status: string) => void
}

export interface IStepsInject {
  props: IStepsProps
  steps: Ref<StepItemState[]>
  addStep: (item: StepItemState) => void
  removeStep: (uid: number) => void
}

defineOptions({
  name: 'LtStep',
})

const props = defineProps(stepProps)
const ns = useNamespace('step')
const index = ref(-1)
const lineStyle = ref({})
const internalStatus = ref('')
const parent = inject('LtSteps') as IStepsInject
const currentInstance = getCurrentInstance()

const lineParentStyle = ref({})
const contentRef = ref<any>()
const wrapperRef = ref<any>()
const iconRef = ref<any>()

onMounted(() => {
  watch(
    [
      () => parent.props.active,
      () => parent.props.processStatus,
      () => parent.props.finishStatus,
    ],
    ([active]) => {
      updateStatus(active)
      nextTick(updateStyles)
    },
    { immediate: true }
  )
})

onBeforeUnmount(() => {
  parent.removeStep(stepItemState.uid)
})

const currentStatus = computed(() => {
  return props.status || internalStatus.value
})

const prevStatus = computed(() => {
  const prevStep = parent.steps.value[index.value - 1]
  return prevStep ? prevStep.currentStatus : 'wait'
})

const isCenter = computed(() => {
  return parent.props.alignCenter
})

const isVertical = computed(() => {
  return parent.props.direction === 'vertical'
})

const isSimple = computed(() => {
  return parent.props.simple
})

const stepsCount = computed(() => {
  return parent.steps.value.length
})

const isLast = computed(() => {
  return parent.steps.value[stepsCount.value - 1]?.uid === currentInstance?.uid
})

const space = computed(() => {
  return isSimple.value ? '' : parent.props.space
})

const containerKls = computed(() => {
  return [
    ns.b(),
    ns.is(isSimple.value ? 'simple' : parent.props.direction),
    ns.is('flex', isLast.value && !space.value && !isCenter.value),
    ns.is('center', isCenter.value && !isVertical.value && !isSimple.value),
  ]
})

const style = computed(() => {
  const style: CSSProperties = {
    flexBasis: isNumber(space.value)
      ? `${space.value}px`
      : space.value
      ? space.value
      : `${100 / (stepsCount.value - (isCenter.value ? 0 : 1))}%`,
  }
  if (isVertical.value) return style
  if (isLast.value) {
    style.maxWidth = `${100 / stepsCount.value}%`
  }
  return style
})

const setIndex = (val: number) => {
  index.value = val
}

const calcProgress = (status: string) => {
  const isWait = status === 'wait'
  const style: CSSProperties = {
    transitionDelay: `${isWait ? '-' : ''}${150 * index.value}ms`,
  }
  const step = status === parent.props.processStatus || isWait ? 0 : 100

  style.borderWidth = step && !isSimple.value ? '1px' : 0
  style[parent.props.direction === 'vertical' ? 'height' : 'width'] = `${step}%`
  lineStyle.value = style
}

const updateStatus = (activeIndex: number) => {
  if (activeIndex > index.value) {
    internalStatus.value = parent.props.finishStatus
  } else if (activeIndex === index.value && prevStatus.value !== 'error') {
    internalStatus.value = parent.props.processStatus
  } else {
    internalStatus.value = 'wait'
  }
  const prevChild = parent.steps.value[index.value - 1]
  if (prevChild) prevChild.calcProgress(internalStatus.value)
}

// 调整element-ui的样式
const updateStyles = () => {
  const vertical = parent.props.direction === 'vertical'
  const wrapperWidth = wrapperRef.value.clientWidth // 最外层的宽度
  const contentWidth = contentRef.value.clientWidth // 内容的宽度
  const contentHeight = contentRef.value.clientHeight // 内容的高度
  const iconWidth = iconRef.value.clientWidth // icon的宽度
  const iconHeight = iconRef.value.clientHeight // icon的高度

  iconRef.value.style.margin = vertical ? '10px 0' : '0' // 设置icon的margin

  lineParentStyle.value = {
    height: vertical ? `$calc(${contentRef.value.clientHeight}px / 2)` : '1px', // 1px 横线的宽度
    width: vertical
      ? '1px'
      : `calc(${wrapperWidth}px - ${contentWidth}px - ${iconWidth}px - 15px - 10px)`, // 15px 是横线距离icon的左边距，10px 是icon的右边距
    left: vertical
      ? `calc(${iconWidth}px / 2)`
      : `calc(${contentWidth}px + 15px + ${iconWidth}px)`, //15px 是横线距离icon的左边距，左移需要加上
    top: vertical
      ? `calc(${iconHeight}px + 20px)` // 20px 是竖线距离icon的上边距10px和下边距10px，下移需要加20px
      : `calc(${contentHeight}px / 2)`,
  }
}

const stepItemState = reactive({
  uid: currentInstance!.uid,
  currentStatus,
  setIndex,
  calcProgress,
})

parent.addStep(stepItemState)
</script>
